// MaturaQuadraticFactorClosedTask.js
// Zadanie maturalne zamknięte – funkcja kwadratowa:
// dane jedno miejsce zerowe i oś symetrii,
// wybór poprawnego wzoru w postaci iloczynowej.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// zapis nawiasu (x - r) lub (x + |r|)
function rootToFactor(r) {
  if (r === 0) return "(x)";
  if (r > 0) return `(x - ${r})`;
  return `(x + ${Math.abs(r)})`;
}

// format ułamka / liczby do LaTeX-a, z minusem
function formatFractionLatex(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const absNum = Math.abs(num);
  const absDen = Math.abs(den);

  if (absDen === 1) {
    return (num < 0 ? "-" : "") + String(absNum);
  }

  const core = `\\dfrac{${absNum}}{${absDen}}`;
  return num < 0 ? `-${core}` : core;
}

// współczynnik przed x^2: chcemy "", "-", "2", "\dfrac{3}{2}", ...
function buildCoeffForX2(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const val = num / den;
  if (val === 1) return "";
  if (val === -1) return "-";
  return formatFractionLatex(num, den);
}

// współczynnik przed iloczynem w postaci iloczynowej
function buildCoeffForFactor(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const val = num / den;
  if (val === 1) return "";
  if (val === -1) return "-";
  return formatFractionLatex(num, den);
}

// ======================= GENERATOR =======================

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 14.1:
 *   f(x) = 1/2 x^2 + bx + c, jedno miejsce zerowe = 6, oś symetrii x = 1.
 *   Drugie miejsce zerowe: −4  →  C. f(x) = 1/2 (x+4)(x−6)
 */
function createOriginalTask() {
  const options = [
    { label: "A", latex: "f(x) = \\dfrac{1}{2}(x - 4)(x - 6)", isCorrect: false },
    { label: "B", latex: "f(x) = \\dfrac{1}{2}(x - 4)(x + 6)", isCorrect: false },
    { label: "C", latex: "f(x) = \\dfrac{1}{2}(x + 4)(x - 6)", isCorrect: true },
    { label: "D", latex: "f(x) = \\dfrac{1}{2}(x + 4)(x + 6)", isCorrect: false },
  ];
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaQuadraticFactorClosedTask",
    data: {
      axis: 1,
      r1: 6,
      r2: -4,
      givenRoot: 6,
      otherRoot: -4,
      aNum: 1,
      aDen: 2,
      aValueLatex: "\\dfrac{1}{2}",
      aCoeffX2Latex: "\\dfrac{1}{2}",
      aCoeffFactorLatex: "\\dfrac{1}{2}",
      generalLatex: "f(x) = \\dfrac{1}{2}x^{2} + bx + c",
      options,
      correctIndex,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: "14.1",
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  // Oś symetrii p – prosta, całkowita i niedaleka od zera
  const possibleAxes = [-4, -3, -2, -1, 1, 2, 3, 4];
  const possibleDists = [1, 2, 3];

  let axis;
  let dist;
  let r1;
  let r2;

  // Losujemy tak długo, aż obydwa miejsca zerowe będą różne od 0
  // i różne od siebie (na wszelki wypadek).
  while (true) {
    axis = pick(possibleAxes);
    dist = pick(possibleDists);

    r1 = axis + dist;
    r2 = axis - dist;

    if (r1 !== 0 && r2 !== 0 && r1 !== r2) {
      break;
    }
  }

  // W treści podamy jedno z miejsc zerowych (losowo)
  const givenRoot = pick([r1, r2]);
  const otherRoot = givenRoot === r1 ? r2 : r1;

  const factor1 = rootToFactor(r1);
  const factor2 = rootToFactor(r2);

  // Losujemy a z przedziału [-3, 3] co 0.5, z wyłączeniem zera
  // a = k/2, k = -6,…,-1,1,…,6
  const possibleNums = [-6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6];
  let aNum = pick(possibleNums);
  let aDen = 2;

  // upraszczamy, jeśli liczba całkowita
  if (aNum % 2 === 0) {
    aNum = aNum / 2;
    aDen = 1;
  }

  const aValueLatex = formatFractionLatex(aNum, aDen);
  const aCoeffX2Latex = buildCoeffForX2(aNum, aDen);
  const aCoeffFactorLatex = buildCoeffForFactor(aNum, aDen);

  // wzór ogólny z wylosowanym a
  const generalLatex =
    aCoeffX2Latex === ""
      ? "f(x) = x^{2} + bx + c"
      : `f(x) = ${aCoeffX2Latex}x^{2} + bx + c`;

  // f(x) = a(x - r1)(x - r2)
  const correctLatex = `f(x) = ${aCoeffFactorLatex}${factor1}${factor2}`;

  // typowe pomyłki – zmiana znaków w jednym lub obu nawiasach
  const wrong1Latex = `f(x) = ${aCoeffFactorLatex}${rootToFactor(-r1)}${factor2}`;
  const wrong2Latex = `f(x) = ${aCoeffFactorLatex}${factor1}${rootToFactor(-r2)}`;
  const wrong3Latex = `f(x) = ${aCoeffFactorLatex}${rootToFactor(-r1)}${rootToFactor(-r2)}`;

  const rawOptions = [
    { latex: correctLatex, isCorrect: true },
    { latex: wrong1Latex, isCorrect: false },
    { latex: wrong2Latex, isCorrect: false },
    { latex: wrong3Latex, isCorrect: false },
  ];

  const options = shuffle(rawOptions).map((opt, idx) => ({
    label: LETTERS[idx],
    latex: opt.latex,
    isCorrect: opt.isCorrect,
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaQuadraticFactorClosedTask",
    data: {
      axis,
      r1,
      r2,
      givenRoot,
      otherRoot,
      aNum,
      aDen,
      aValueLatex,
      aCoeffX2Latex,
      aCoeffFactorLatex,
      generalLatex,
      options,
      correctIndex,
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_14_1" },
  };
}


// ======================= RENDER TREŚCI =======================

export function renderTask({ data }) {
  const { axis, givenRoot, options, generalLatex } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Funkcja kwadratowa <InlineMath math={"f"} /> jest określona wzorem{" "}
        <InlineMath math={generalLatex} />, gdzie{" "}
        <InlineMath math={"b, c"} /> są liczbami rzeczywistymi.
      </p>
      <p>
        Jednym z miejsc zerowych funkcji <InlineMath math={"f"} /> jest liczba{" "}
        <InlineMath math={`${givenRoot}`} />. W kartezjańskim układzie
        współrzędnych prosta o równaniu{" "}
        <InlineMath math={`x = ${axis}`} /> jest osią symetrii wykresu funkcji{" "}
        <InlineMath math={"f"} />.
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Funkcja <InlineMath math={"f"} /> jest określona wzorem:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}


// ======================= INPUT (ABCD) =======================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ======================= SPRAWDZANIE =======================

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

// ======================= ODPOWIEDŹ I WYJAŚNIENIE =======================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  return (
    <p style={{ textAlign: "left" }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </p>
  );
}

export function renderExplanation({ data }) {
  const { axis, givenRoot, otherRoot, aValueLatex } = data;

  const p = axis;
  const x1 = givenRoot;
  const x2 = otherRoot;

  // (x - r) zapisujemy ładnie: jeśli r < 0, to (x + |r|)
  const factorTerm = (r) => {
    if (r < 0) return `(x + ${Math.abs(r)})`;
    if (r > 0) return `(x - ${r})`;
    return `(x)`;
  };

  // ładne przejście w obliczeniu x2: 2p - (−k) -> 2p + k
  const x2Middle =
    x1 < 0 ? `2 \\cdot ${p} + ${Math.abs(x1)}` : `2 \\cdot ${p} - ${x1}`;

  const factorLatex = `f(x) = ${aValueLatex}${factorTerm(x1)}${factorTerm(x2)}`;

  return (
    <div style={{ textAlign: "left" }} className="space-y-3">
      <p>
        <strong>Krok 1.</strong> Współczynnik{" "}
        <InlineMath math={'a'} /> nie zmienia położenia miejsc zerowych — o nich
        decydują nawiasy w postaci iloczynowej.
      </p>

      <p>
        <strong>Krok 2.</strong> Znamy jedno miejsce zerowe{" "}
        <InlineMath math={`x_1 = ${x1}`} /> oraz oś symetrii{" "}
        <InlineMath math={`p = ${p}`} />. Miejsca zerowe są symetryczne względem osi:
      </p>

      <BlockMath math={'p = \\dfrac{x_1 + x_2}{2}'} />

      <p>
        <strong>Krok 3.</strong> Wyznaczamy drugie miejsce zerowe:
      </p>

      <BlockMath math={`x_2 = 2p - x_1`} />
      <BlockMath math={`x_2 = 2 \\cdot ${p} - (${x1})`} />
      <BlockMath math={`x_2 = ${x2Middle} = ${x2}`} />

      <p>
        <strong>Krok 4.</strong> Zapisujemy funkcję w postaci iloczynowej, pilnując
        znaków w nawiasach (np. <InlineMath math={'x - (-3) = x + 3'} />):
      </p>

      <BlockMath math={factorLatex} />
    </div>
  );
}


export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem", textAlign: "left" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}
